import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import {
  PrismButton,
  PrismNumberInput,
  PrismTooltip,
} from '@mylighthouse/prism-ember';
import type {
  CustomEmberTableColumn,
  CustomEmberTableRow,
} from '@mylighthouse/prism-ember/types/components/prism/table';
import { task } from 'ember-concurrency';
import { eq } from 'ember-truth-helpers';
import type { RecommendationStrategy } from 'lumen/models';
import type {
  ComputedValueCondition,
  OperandCondition,
  UserEditableCondition,
} from 'lumen/models/recommendation-strategy';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { timeout } from 'lumen/utils/waiters';
import { localCopy } from 'tracked-toolbox';

import styles from './td-dow.module.css';

export type StrategyTableColumn = CustomEmberTableColumn & {
  id: number;
  isEditing: boolean;
  isSaveStrategyTaskRunning: boolean;
  onCopyToAllDays: (
    setting: 'maximum' | 'minimum',
    isConditional: boolean,
    value: number | null,
  ) => void;
  onDayOfWeekValueChange: (
    dow: number,
    setting: string,
    isConditional: boolean,
    value: number | null,
  ) => void;
  saveAttempted: boolean;
  strategy: RecommendationStrategy;
};

export type StrategyTableRow = CustomEmberTableRow & {
  label: string;
  setting: 'condition' | 'maximum' | 'minimum';
};

interface SettingsRecommendationsPriceRangeTdDowSignature {
  Args: {
    column: StrategyTableColumn;
    row: StrategyTableRow;
    value: {
      formattedValue: string;
      isConditional: boolean;
      rawValue: number;
    };
  };
}

export default class SettingsRecommendationsPriceRangeTdDow extends Component<SettingsRecommendationsPriceRangeTdDowSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];

  // Tracks the value currently in the input — input changes are debounced before
  // committing to the strategy, so `@value.rawValue` is stale until the user
  // blurs or the debounce fires. Copy-to-all-days uses this live value.
  // `@localCopy` re-seeds it whenever `@value.rawValue` changes externally
  // (e.g. another column's copy-to-all-days) so it never goes stale.
  @localCopy('args.value.rawValue') declare liveValue: number | null;

  /*
    The input changes are debounced only when typing
    On focus out, the value is set immediately to check for errors
  */
  debouncedOnDayOfWeekValueChangeTask = task(
    { restartable: true },
    async (dowValue: number | null): Promise<void> => {
      await timeout('extra-long');
      this.setDayOfWeekValue(dowValue);
    },
  );

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get conditions() {
    return this.args.column.strategy.conditionalStrategies?.[0]
      ?.conditions as OperandCondition & {
      first?: ComputedValueCondition;
      second?: UserEditableCondition;
    };
  }

  get copyLabel(): string {
    return this.intl.t(
      'pricing_assistant.recommendations.price_range.copy_to_all_days',
    );
  }

  // Input field is disabled if no conditional value is set or save strategy task is running
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get numberInputDisabled() {
    if (this.args.column.isSaveStrategyTaskRunning) {
      return true;
    }

    return (
      this.args.value.isConditional && this.conditions?.second?.value === null
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get numberInputHasErrors() {
    if (!this.args.column.saveAttempted) {
      return false;
    }

    return this.args.column.strategy.priceRangeStrategyDayOfWeekWithErrors.some(
      (day) =>
        day.dow === this.args.column.id &&
        day.isConditional === this.args.value.isConditional,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get numberInputPlaceholder() {
    if (this.args.row.setting === 'minimum') {
      return this.intl.t(
        'pricing_assistant.recommendations.price_range.no_min',
      );
    }

    if (this.args.row.setting === 'maximum') {
      return this.intl.t(
        'pricing_assistant.recommendations.price_range.no_max',
      );
    }

    return '';
  }

  setDayOfWeekValue(dowValue: number | null): void {
    const { column, row, value } = this.args;

    this.args.column.onDayOfWeekValueChange(
      column.id,
      row.setting,
      value.isConditional,
      dowValue,
    );
  }

  @action onCopyToAllDays(): void {
    const { column, row, value } = this.args;
    // The copy button only renders for minimum/maximum rows, so this guard is
    // defensive — it also narrows `setting` to the type onCopyToAllDays expects.
    if (row.setting === 'condition') {
      return;
    }
    column.onCopyToAllDays(row.setting, value.isConditional, this.liveValue);
  }

  @action onDayOfWeekFocusOut(event: Event): void {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.debouncedOnDayOfWeekValueChangeTask.cancelAll();
    const target = event.target as HTMLInputElement;
    const value = target?.value ? parseInt(target.value, 10) : null;
    // Skip the commit when nothing changed — otherwise blurring an untouched
    // input rewrites the strategy and marks it dirty (activating Save).
    if (value === this.args.value.rawValue) {
      return;
    }
    this.setDayOfWeekValue(value);
  }

  @action onDayOfWeekValueChange(dowValue: number | null): void {
    this.liveValue = dowValue;
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.debouncedOnDayOfWeekValueChangeTask.perform(dowValue);
  }

  <template>
    {{#unless (eq @row.setting "condition")}}
      {{#if @column.isEditing}}
        <div class={{styles.cell-wrapper}}>
          <div class={{styles.input-slot}}>
            <PrismNumberInput
              @disabled={{this.numberInputDisabled}}
              @hasErrors={{this.numberInputHasErrors}}
              @inlineLabel={{this.hotelAndMarketService.hotel.currencySymbol}}
              @isSmall={{true}}
              @isTouched={{true}}
              @min={{0}}
              @onFocusOut={{this.onDayOfWeekFocusOut}}
              @onValueChange={{this.onDayOfWeekValueChange}}
              @placeholder={{this.numberInputPlaceholder}}
              @removePadding={{true}}
              @value={{@value.rawValue}}
            />
          </div>
          <span class={{styles.copy-wrapper}}>
            <PrismButton
              @buttonType="secondary"
              @icon="copy"
              @isDisabled={{this.numberInputDisabled}}
              @onClick={{this.onCopyToAllDays}}
              @size="small"
              aria-label={{this.copyLabel}}
              data-test-copy-to-all-days
            />
            <PrismTooltip @side="top" @text={{this.copyLabel}} />
          </span>
        </div>
      {{else}}
        {{@value.formattedValue}}
      {{/if}}
    {{/unless}}
  </template>
}
